import { AnimatePresence, motion } from "framer-motion";
import { type FC, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import type { PlanSnapshotAttachment } from "../../types";

import { formatBytes } from "../../utils/currency";
import { formatDateTime } from "../../utils/time";
import { EmptyState } from "../common";
import { Button } from "../common/Button";
import { SectionHeader } from "../common/SectionHeader";
import { TEXT_CLASSES, Text } from "../common/Text";

export interface PlanSnapshotDrawerAttachmentListItem {
  filename: string;
  attachment: PlanSnapshotAttachment | null;
}

export interface PlanSnapshotDrawerAttachmentListProps {
  items: PlanSnapshotDrawerAttachmentListItem[];
  selectedAttachment: string | null;
  onSelectAttachment: (filename: string | null) => void;
}

function getContentUrl(
  attachment: PlanSnapshotAttachment | null,
): string | null {
  if (!attachment?.snapshot_id || !attachment.id) {
    return null;
  }
  return `/api/plan-snapshots/${encodeURIComponent(attachment.snapshot_id)}/attachments/${encodeURIComponent(attachment.id)}/content`;
}

function isPreviewableImage(
  attachment: PlanSnapshotAttachment | null,
): boolean {
  if (!attachment?.mime_type) {
    return false;
  }
  return (
    attachment.mime_type.startsWith("image/") &&
    Boolean(getContentUrl(attachment))
  );
}

function FileIcon({ className }: { className?: string }) {
  return (
    <svg
      className={
        className ?? "h-3.5 w-3.5 shrink-0 text-gray-400 dark:text-white/40"
      }
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M15.621 4.379a3 3 0 0 0-4.242 0l-7 7a3 3 0 0 0 4.241 4.243h.001l.497-.5a.75.75 0 0 1 1.064 1.057l-.498.501a4.5 4.5 0 0 1-6.364-6.364l7-7a4.5 4.5 0 0 1 6.368 6.36l-3.455 3.553A2.625 2.625 0 1 1 9.52 9.52l3.45-3.451a.75.75 0 1 1 1.061 1.06l-3.45 3.451a1.125 1.125 0 0 0 1.587 1.595l3.454-3.553a3 3 0 0 0 0-4.242Z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function DownloadIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className ?? "h-3 w-3"}
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M10.75 2.75a.75.75 0 0 0-1.5 0v8.614L6.295 8.235a.75.75 0 1 0-1.09 1.03l4.25 4.5a.75.75 0 0 0 1.09 0l4.25-4.5a.75.75 0 0 0-1.09-1.03l-2.955 3.129V2.75Z" />
      <path d="M3.5 12.75a.75.75 0 0 0-1.5 0v2.5A2.75 2.75 0 0 0 4.75 18h10.5A2.75 2.75 0 0 0 18 15.25v-2.5a.75.75 0 0 0-1.5 0v2.5c0 .69-.56 1.25-1.25 1.25H4.75c-.69 0-1.25-.56-1.25-1.25v-2.5Z" />
    </svg>
  );
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className ?? "h-3.5 w-3.5"}
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
    </svg>
  );
}

function AttachmentThumbnail({
  attachment,
  size = "sm",
}: {
  attachment: PlanSnapshotAttachment | null;
  size?: "sm" | "md";
}) {
  const [failed, setFailed] = useState(false);
  const contentUrl = getContentUrl(attachment);
  const previewable = isPreviewableImage(attachment) && !failed;

  const sizeClass =
    size === "sm"
      ? "h-7 w-7 shrink-0 rounded-none border border-slate-300 dark:border-slate-700"
      : "h-9 w-9 shrink-0 rounded-none border border-slate-300 dark:border-slate-700";

  if (previewable && contentUrl) {
    return (
      <img
        src={contentUrl}
        alt=""
        onError={() => setFailed(true)}
        className={`${sizeClass} bg-slate-100 object-cover dark:bg-slate-900`}
      />
    );
  }

  return (
    <div
      className={`${sizeClass} flex items-center justify-center bg-slate-100 dark:bg-slate-900`}
    >
      <FileIcon
        className={
          size === "sm"
            ? "h-3.5 w-3.5 text-gray-400 dark:text-white/40"
            : "h-4 w-4 text-gray-500 dark:text-white/60"
        }
      />
    </div>
  );
}

const LAYOUT_TRANSITION = {
  layout: { duration: 0.3, ease: [0.4, 0, 0.2, 1] as const },
};

const CONTENT_SLIDE_VARIANTS = {
  center: { opacity: 1, x: 0 },
  enter: (direction: number) => ({ opacity: 0, x: direction > 0 ? 60 : -60 }),
  exit: (direction: number) => ({ opacity: 0, x: direction > 0 ? -60 : 60 }),
};

const CONTENT_TRANSITION = { duration: 0.2, ease: [0.4, 0, 0.2, 1] as const };

// ---------------------------------------------------------------------------
// Chevron button
// ---------------------------------------------------------------------------

function ChevronButton({
  direction,
  disabled,
  onClick,
}: {
  direction: "prev" | "next";
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <Button
      type="button"
      onClick={onClick}
      disabled={disabled}
      variant="ghost"
      size="sm"
      className={`h-8 w-8 p-0 sm:h-6 sm:w-6 ${
        disabled
          ? "opacity-20"
          : "text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-white/10 dark:hover:text-white"
      }`}
      aria-label={
        direction === "prev" ? "Previous attachment" : "Next attachment"
      }
    >
      <svg
        className="h-3.5 w-3.5"
        viewBox="0 0 20 20"
        fill="currentColor"
        aria-hidden="true"
      >
        {direction === "prev" ? (
          <path
            fillRule="evenodd"
            d="M11.78 5.22a.75.75 0 0 1 0 1.06L8.06 10l3.72 3.72a.75.75 0 1 1-1.06 1.06l-4.25-4.25a.75.75 0 0 1 0-1.06l4.25-4.25a.75.75 0 0 1 1.06 0Z"
            clipRule="evenodd"
          />
        ) : (
          <path
            fillRule="evenodd"
            d="M8.22 5.22a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 0 1-1.06-1.06L11.94 10 8.22 6.28a.75.75 0 0 1 0-1.06Z"
            clipRule="evenodd"
          />
        )}
      </svg>
    </Button>
  );
}

// ---------------------------------------------------------------------------
// Modal body content (animated per-item)
// ---------------------------------------------------------------------------

function AttachmentModalContent({
  item,
}: {
  item: PlanSnapshotDrawerAttachmentListItem;
}) {
  const { attachment } = item;
  const url = attachment?.url.trim() ?? "";
  const hasUrl = url.length > 0;
  const contentUrl = getContentUrl(attachment);
  const previewable = isPreviewableImage(attachment);
  const downloadHref = contentUrl ?? (hasUrl ? url : null);

  return (
    <>
      <div className="custom-scrollbar flex-1 overflow-y-auto px-3 py-2.5 sm:px-4 sm:py-2.5">
        {previewable && contentUrl ? (
          <div className="mb-3 flex items-center justify-center rounded-none border border-slate-300 bg-slate-50 p-2 dark:border-slate-700 dark:bg-slate-900/40">
            <img
              src={contentUrl}
              alt={item.filename}
              className="max-h-36 rounded-none object-contain sm:max-h-48"
            />
          </div>
        ) : null}

        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
          {attachment?.mime_type ? (
            <div>
              <dt className="text-xs uppercase tracking-wide text-gray-400 dark:text-gray-500">
                Type
              </dt>
              <dd className="mt-0.5 text-gray-600 dark:text-gray-400">
                {attachment.mime_type}
              </dd>
            </div>
          ) : null}
          {attachment?.size != null ? (
            <div>
              <dt className="text-xs uppercase tracking-wide text-gray-400 dark:text-gray-500">
                Size
              </dt>
              <dd className="mt-0.5 text-gray-600 dark:text-gray-400">
                {formatBytes(attachment.size)}
              </dd>
            </div>
          ) : null}
          {attachment?.created_at ? (
            <div>
              <dt className="text-xs uppercase tracking-wide text-gray-400 dark:text-gray-500">
                Created
              </dt>
              <dd className="mt-0.5 text-gray-600 dark:text-gray-400">
                {formatDateTime(attachment.created_at)}
              </dd>
            </div>
          ) : null}
          {attachment?.updated_at ? (
            <div>
              <dt className="text-xs uppercase tracking-wide text-gray-400 dark:text-gray-500">
                Updated
              </dt>
              <dd className="mt-0.5 text-gray-600 dark:text-gray-400">
                {formatDateTime(attachment.updated_at)}
              </dd>
            </div>
          ) : null}
        </dl>

        {attachment?.description ? (
          <div className="mt-3">
            <h5 className="text-xs uppercase tracking-wide text-gray-400 dark:text-gray-500">
              Description
            </h5>
            <p className="custom-scrollbar mt-1 max-h-28 overflow-y-auto whitespace-pre-wrap text-xs leading-relaxed text-gray-600 sm:max-h-40 dark:text-gray-300">
              {attachment.description}
            </p>
          </div>
        ) : null}
      </div>

      {hasUrl || downloadHref ? (
        <div className="flex shrink-0 flex-wrap items-center gap-2 border-t border-gray-200 px-3 py-2.5 sm:px-4 dark:border-white/10">
          {hasUrl ? (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-none border border-emerald-500 bg-emerald-600 px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-widest text-white transition hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 dark:bg-emerald-500 dark:text-slate-950 dark:hover:bg-emerald-400"
            >
              Open attachment
              <svg
                className="h-3 w-3"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M4.25 5.5a.75.75 0 0 0-.75.75v8.5c0 .414.336.75.75.75h8.5a.75.75 0 0 0 .75-.75v-4a.75.75 0 0 1 1.5 0v4A2.25 2.25 0 0 1 12.75 17h-8.5A2.25 2.25 0 0 1 2 14.75v-8.5A2.25 2.25 0 0 1 4.25 4h5a.75.75 0 0 1 0 1.5h-5Z"
                  clipRule="evenodd"
                />
                <path
                  fillRule="evenodd"
                  d="M6.194 12.753a.75.75 0 0 0 1.06.053L16.5 4.44v2.81a.75.75 0 0 0 1.5 0v-4.5a.75.75 0 0 0-.75-.75h-4.5a.75.75 0 0 0 0 1.5h2.553l-9.056 8.194a.75.75 0 0 0-.053 1.06Z"
                  clipRule="evenodd"
                />
              </svg>
            </a>
          ) : null}
          {downloadHref ? (
            <a
              href={downloadHref}
              download={attachment?.filename ?? item.filename}
              className="inline-flex items-center gap-1.5 rounded-none border border-slate-300 bg-white px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-widest text-slate-700 transition hover:border-emerald-500 hover:bg-emerald-50 hover:text-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:border-emerald-500 dark:hover:bg-emerald-500/10 dark:hover:text-emerald-300 dark:focus:ring-emerald-500/20"
            >
              Download
              <DownloadIcon />
            </a>
          ) : null}
        </div>
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------------------
// Detail modal (portal with shared layout animation + navigation)
// ---------------------------------------------------------------------------

function AttachmentDetailModal({
  items,
  currentIndex,
  onNavigate,
  onClose,
}: {
  items: PlanSnapshotDrawerAttachmentListItem[];
  currentIndex: number;
  onNavigate: (filename: string) => void;
  onClose: () => void;
}) {
  const directionRef = useRef(0);

  const item = items[currentIndex];
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < items.length - 1;

  const goPrev = useCallback(() => {
    if (currentIndex > 0) {
      directionRef.current = -1;
      onNavigate(items[currentIndex - 1].filename);
    }
  }, [currentIndex, items, onNavigate]);

  const goNext = useCallback(() => {
    if (currentIndex < items.length - 1) {
      directionRef.current = 1;
      onNavigate(items[currentIndex + 1].filename);
    }
  }, [currentIndex, items, onNavigate]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, []);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
      if (e.key === "ArrowLeft") {
        goPrev();
      }
      if (e.key === "ArrowRight") {
        goNext();
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose, goPrev, goNext]);

  return createPortal(
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-2 backdrop-blur-sm sm:p-3"
      onClick={onClose}
    >
      <motion.div
        layoutId={`attachment-card-${items[currentIndex].filename}`}
        className="relative flex max-h-[85vh] w-full max-w-sm flex-col overflow-hidden rounded-none border border-slate-300 bg-white shadow-2xl sm:max-h-[70vh] sm:max-w-md dark:border-slate-700 dark:bg-slate-950"
        onClick={(e) => e.stopPropagation()}
        transition={LAYOUT_TRANSITION}
      >
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b border-slate-300 px-3 py-2.5 sm:px-4 dark:border-slate-700">
          <div className="flex min-w-0 items-center gap-2">
            <AttachmentThumbnail attachment={item.attachment} />
            <AnimatePresence mode="wait" custom={directionRef.current}>
              <motion.span
                key={item.filename}
                custom={directionRef.current}
                variants={CONTENT_SLIDE_VARIANTS}
                initial="enter"
                animate="center"
                exit="exit"
                transition={CONTENT_TRANSITION}
                className={`truncate ${TEXT_CLASSES.label} text-slate-900 dark:text-white`}
              >
                {item.filename}
              </motion.span>
            </AnimatePresence>
          </div>
          <div className="flex shrink-0 items-center gap-0.5">
            <ChevronButton
              direction="prev"
              disabled={!hasPrev}
              onClick={goPrev}
            />
            <ChevronButton
              direction="next"
              disabled={!hasNext}
              onClick={goNext}
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="ml-0.5 h-8 w-8 p-0 text-slate-400 hover:bg-slate-100 hover:text-slate-700 sm:h-6 sm:w-6 dark:hover:bg-slate-800 dark:hover:text-white"
              aria-label="Close"
            >
              <CloseIcon />
            </Button>
          </div>
        </div>

        {/* Body with animated content transitions */}
        <AnimatePresence
          mode="wait"
          custom={directionRef.current}
          initial={false}
        >
          <motion.div
            key={item.filename}
            custom={directionRef.current}
            variants={CONTENT_SLIDE_VARIANTS}
            initial="enter"
            animate="center"
            exit="exit"
            transition={CONTENT_TRANSITION}
            className="flex min-h-0 flex-1 flex-col"
          >
            <AttachmentModalContent item={item} />
          </motion.div>
        </AnimatePresence>
      </motion.div>
    </motion.div>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// Card (list item with shared layoutId)
// ---------------------------------------------------------------------------

function AttachmentCard({
  item,
  onSelect,
}: {
  item: PlanSnapshotDrawerAttachmentListItem;
  onSelect: () => void;
}) {
  const { filename, attachment } = item;

  return (
    <motion.button
      type="button"
      layoutId={`attachment-card-${filename}`}
      onClick={onSelect}
      transition={LAYOUT_TRANSITION}
      className="flex w-full items-center gap-2 rounded-none border border-slate-300 bg-white px-2.5 py-1.5 text-left transition-all hover:border-emerald-500 hover:bg-emerald-50/40 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 dark:border-slate-700 dark:bg-slate-900/70 dark:hover:border-emerald-500 dark:hover:bg-emerald-500/10"
      title="View attachment details"
    >
      <AttachmentThumbnail attachment={attachment} />
      <div className="min-w-0 flex-1">
        <span className="block truncate text-xs font-semibold text-gray-800 dark:text-gray-100">
          {filename}
        </span>
        {attachment?.description && (
          <Text as="p" className="line-clamp-1 leading-tight">
            {attachment.description}
          </Text>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-slate-500 dark:text-slate-400">
        {attachment?.mime_type && (
          <span>{attachment.mime_type.split("/").pop()}</span>
        )}
        {attachment?.size != null && (
          <span>{formatBytes(attachment.size)}</span>
        )}
      </div>
    </motion.button>
  );
}

// ---------------------------------------------------------------------------
// List
// ---------------------------------------------------------------------------

export const PlanSnapshotDrawerAttachmentList: FC<
  PlanSnapshotDrawerAttachmentListProps
> = ({ items, selectedAttachment, onSelectAttachment }) => {
  const selectedIndex =
    selectedAttachment !== null
      ? items.findIndex((item) => item.filename === selectedAttachment)
      : -1;
  const resolvedIndex = selectedIndex >= 0 ? selectedIndex : null;

  return (
    <section>
      <SectionHeader
        variant="default"
        title="Attachments"
        badge={String(items.length)}
        className="mb-0 text-xs"
      />
      {items.length === 0 ? (
        <EmptyState
          size="sm"
          title="No attachments"
          description="This task does not reference any attachments."
        />
      ) : (
        <div className="space-y-1">
          {items.map((item) => (
            <AttachmentCard
              key={item.filename}
              item={item}
              onSelect={() => onSelectAttachment(item.filename)}
            />
          ))}
        </div>
      )}

      <AnimatePresence>
        {resolvedIndex != null ? (
          <AttachmentDetailModal
            items={items}
            currentIndex={resolvedIndex}
            onNavigate={onSelectAttachment}
            onClose={() => onSelectAttachment(null)}
          />
        ) : null}
      </AnimatePresence>
    </section>
  );
};
